"use client";

import { ArrowUpRight, BookOpen, ChevronDown, Globe } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { cn } from "@/lib/utils";
import {
  hasRenderableContent,
  isExternalSource,
  type MessageSource,
  rootDomain,
  sourceFaviconUrl,
  sourceHost,
  sourceTitle,
} from "./source-utils";

const MAX_STACKED_AVATARS = 4;

/** Distinct source icons for the collapsed pill's avatar stack: one per unique
 *  domain (external) plus one shared icon for internal help/news refs, capped
 *  at {@link MAX_STACKED_AVATARS}. */
function stackedAvatars(
  sources: MessageSource[],
): { key: string; favicon: string | null; external: boolean }[] {
  const seen = new Set<string>();
  const out: { key: string; favicon: string | null; external: boolean }[] = [];
  for (const s of sources) {
    const link = s.link?.trim() || undefined;
    const external = isExternalSource(link);
    const key = external ? (rootDomain(sourceHost(link)) ?? link ?? "external") : "internal";
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ key, favicon: sourceFaviconUrl(link), external });
    if (out.length >= MAX_STACKED_AVATARS) break;
  }
  return out;
}

/**
 * The "Sources" block under an AI reply — the help/news pages the answer drew
 * from (the reply text itself is clean, no raw URLs). Collapsed by default to
 * a small `Sources · N` pill; expands to a flat list of rows (favicon · link ·
 * external arrow). Hovering a row reveals a tooltip with the source's title +
 * description. On touch there's no hover, but a tap opens the link directly.
 *
 * Clicks route through `onOpen(link)`: the parent (message bubble) sends
 * internal `/help` · `/news` paths through the in-widget viewer and opens
 * absolute URLs in a new tab — the same dispatch the reply's inline links use.
 */
export function MessageSources({
  sources,
  onOpen,
}: {
  sources: MessageSource[];
  onOpen: (link: string) => void;
}) {
  const t = useTranslations("messages");
  const [expanded, setExpanded] = useState(false);

  const renderable = sources.filter(hasRenderableContent);
  if (renderable.length === 0) return null;

  const avatars = stackedAvatars(renderable);

  return (
    <div className="mt-1.5 flex w-full flex-col items-start gap-1">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className={cn(
          "flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-wx-border bg-wx-bg-elevated py-1 pr-2.5 pl-1.5",
          "font-medium text-[11px] text-wx-fg-muted transition-colors hover:bg-wx-bg-elevated-2 hover:text-wx-fg",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wx-primary",
        )}
      >
        <span className="flex items-center -space-x-1.5">
          {avatars.map((a) => (
            <span
              key={a.key}
              className="inline-flex h-4 w-4 items-center justify-center overflow-hidden rounded-full bg-wx-bg ring-2 ring-wx-bg-elevated"
            >
              <SourceIcon favicon={a.favicon} external={a.external} />
            </span>
          ))}
        </span>
        <span>
          {t("sources")} · {renderable.length}
        </span>
        <ChevronDown
          className={cn("h-3 w-3 transition-transform", expanded && "rotate-180")}
          aria-hidden="true"
        />
      </button>
      {expanded && (
        <div className="flex w-full flex-col">
          {renderable.map((source, idx) => (
            <SourceRow
              // Sources are a fixed, never-reordered list per message.
              // biome-ignore lint/suspicious/noArrayIndexKey: stable per-message list
              key={idx}
              source={source}
              onOpen={onOpen}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SourceRow({ source, onOpen }: { source: MessageSource; onOpen: (link: string) => void }) {
  const link = source.link?.trim() || undefined;
  const external = isExternalSource(link);
  const favicon = sourceFaviconUrl(link);
  const heading = source.seoTitle?.trim();
  const description = source.seoDescription?.trim();
  // Row shows the URL for external sources (as designed); internal paths show
  // their title instead of the raw `/help/…` path.
  const label = external ? (link as string) : (heading ?? link ?? sourceTitle(source));
  const hasTooltip = !!(heading || description);

  const rowInner = (
    <>
      <SourceIcon favicon={favicon} external={external} />
      <span className="min-w-0 flex-1 truncate text-[12px] text-wx-fg">{label}</span>
      {external && (
        <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-wx-fg-subtle" aria-hidden="true" />
      )}
    </>
  );

  const rowClass = "flex items-center gap-2 rounded-md px-2 py-1.5 text-left";

  return (
    <div className="group relative">
      {link ? (
        <button
          type="button"
          onClick={() => onOpen(link)}
          className={cn(
            rowClass,
            "w-full transition-colors hover:bg-wx-bg-elevated",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wx-primary",
          )}
        >
          {rowInner}
        </button>
      ) : (
        <div className={rowClass}>{rowInner}</div>
      )}
      {hasTooltip && (
        // Hover card — inverted (`wx-fg` bg on `wx-bg` text) so it reads as a
        // dark tooltip in light mode and a light one in dark mode. Hover-only;
        // touch users get the link on tap instead.
        <div className="pointer-events-none absolute bottom-full left-2 z-10 mb-1 hidden w-max max-w-[240px] rounded-lg bg-wx-fg px-3 py-2 text-wx-bg shadow-lg group-hover:block">
          {heading && <p className="font-semibold text-[12px] leading-snug">{heading}</p>}
          {description && (
            <p className="mt-0.5 text-[11px] text-wx-bg/70 leading-snug">{description}</p>
          )}
        </div>
      )}
    </div>
  );
}

/** Favicon for external hosts (Google service → generic globe fallback via
 *  `onError`); an in-widget book icon for internal help/news references. */
function SourceIcon({ favicon, external }: { favicon: string | null; external: boolean }) {
  const [broken, setBroken] = useState(false);

  if (external && favicon && !broken) {
    return (
      // biome-ignore lint/performance/noImgElement: arbitrary third-party favicon host; next/image needs an allowlist
      <img
        src={favicon}
        alt=""
        width={14}
        height={14}
        referrerPolicy="no-referrer"
        // Favicons are dark/colored icons designed for light backgrounds, so
        // they vanish on the dark bubble surface in dark mode. Render them as a
        // clean circular chip on a constant white ground (browser-tab
        // convention) — crisp in both themes; `object-contain` letterboxes odd
        // aspect ratios instead of cropping.
        className="h-4 w-4 shrink-0 rounded-full bg-white object-contain p-px ring-1 ring-black/10"
        onError={() => setBroken(true)}
      />
    );
  }
  const Fallback = external ? Globe : BookOpen;
  return <Fallback className="h-4 w-4 shrink-0 text-wx-fg-subtle" aria-hidden="true" />;
}
